import { ReactNode, useEffect, useRef, useState } from "react";
import { Route, Router, Switch } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import browserHistory from "@app/browserHistory";
import Home from "@app/home";
import Layout from "@app/layout";
import { MaintenancePage } from "@app/maintenance";
import NotificationHandlerWrapper from "@app/NotificationHandlerWrapper";
import { provideNotificationContext } from "@app/notifications";
import { ProviderNetworkImportRouter } from "@app/provider-network-imports";
import { NotFound } from "@app/shared/ErrorPages";
import ToolsRouter from "@app/tools/ToolsRouter";
import { AccountRouter } from "@app/user/account";
import * as authentication from "@omnius/signalr/authentication";
import { datadogRum } from "@datadog/browser-rum";
import { UnregisterCallback } from "history";
import { UserClaim } from "@app/admin/user-administration/userModel";
import { NetworkOutreachRouter } from "@app/network-outreach";
import { Client, ClientType } from "@app/client/client";
import AccessReportsRouter from "@app/access-reports/AccessReportsRouter";
import { AdminRouter } from "@app/admin";
import AuthenticationCallback from "@app/authentication/authenticationCallback";
import AuthenticationService from "@app/authentication/authenticationService";
import SilentRefreshCallback from "@app/authentication/silentRefreshCallback";
import { ClientService } from "@app/client/clientService";
import { datadogViewNames } from "@app/datadogViewNames";
import { ExceptionsRouter } from "@app/exceptions";
import NetworkSubmissionRouter from "@app/compliance-connect/network-submission/NetworkSubmissionRouter";
import { FeatureFlagContextProvider, FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import MaintenanceRouter from "@app/maintenance/MaintenanceRouter";
import { MappingContextProvider } from "@app/mapping/MappingContext";
import MemberSetsRouter from "@app/members/MemberSetsRouter";
import { OperationsRouter } from "@app/operations";
import OverviewsRouter from "@app/overviews/OverviewsRouter";
import ComplianceConnectRegulatorRouter from "@app/compliance-connect/regulator/ComplianceConnectRegulatorRouter";
import { PerformanceRouter } from "@app/performance/PerformanceRouter";
import ProjectsRouter from "@app/projects/ProjectsRouter";
import ProviderSetsRouter from "@app/provider-sets/ProviderSetsRouter";
import { ManageReportPresets } from "@app/report-customization/manage-presets/ManageReportPresets";
import { ReportsRouter } from "@app/reports";
import ResourcesRouter from "@app/resources/ResourcesRouter";
import { DeploymentEnvironmentConstants } from "@app/shared/constants";
import ErrorBoundary from "@app/shared/ErrorBoundary";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { TagsRouter } from "@app/tags";
import TargetingRouter from "@app/targeting/TargetingRouter";
import TemplatesRouter from "@app/templates/TemplatesRouter";
import { TerminationsRouter } from "@app/terminations";
import TutorialsRouter from "@app/tutorials/TutorialsRouter";
import { DashboardRestrictedRoute } from "@app/user/DashboardRestricted";
import { DashboardType } from "@app/user/enumerations";
import { InactivityMonitor } from "@app/user/InactivityMonitor";
import Roles from "@app/user/roles";
import { UserInfo, UserContext } from "@app/user/UserContext";
import UserService from "@app/user/userService";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// "Munchkin" object is made available through the reference to munchkin.marketo.net/munchkin.js
// Because it's not explicitly defined, typescript throws a compile time error. This delcaration is meant to bypass that error
// Marketo also doesn't provide any typescript support, but here's their API: https://developers.marketo.com/javascript-api/lead-tracking/
/* oxlint-disable-next-line @typescript-eslint/no-explicit-any */
declare const Munchkin: any;
const marketoMunchkinScriptId = "marketo-munchkin";
const sessionValidationIntervalSeconds = 60; // don't need to validate session too frequently
const dashboardDefaultClaimType = "http://questanalytics.com/claims/dashboarddefault";

const App = (): ReactNode => {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<UserInfo>(undefined);
  const validationTimeout = useRef<number>(undefined);
  const pendoHasInitialized = useRef(false);

  const userService = useRef(new UserService()).current;
  const clientService = useRef(new ClientService()).current;
  const queryClient = useRef(new QueryClient()).current;
  const marketoMunchkinReadyRef = useRef(false);

  const updateClientType = (clientType: ClientType) => {
    setUser(prevUser => ({ ...prevUser, clientType }));
  };

  const checkMarketoMunchkinReady = () => {
    if (marketoMunchkinReadyRef.current)
      return true;

    const isReady = !!document.getElementById(marketoMunchkinScriptId);
    marketoMunchkinReadyRef.current = isReady;

    return isReady;
  };

  const logMarketoMunchkinPageVisit = (pathname: string) => {
    if (!window.enableMarketoMunchkinLogging)
      return;

    if (!pathname || pathname.indexOf("/callback") !== -1 || pathname.indexOf("/silent-refresh-callback") !== -1)
      return;

    if (checkMarketoMunchkinReady() && (typeof (Munchkin) !== "undefined" && Munchkin !== null)) {
      Munchkin.munchkinFunction("visitWebPage", { href: pathname });
    } else {
      loadMarketoMunchkin();
    }
  };

  /**
   * Loads marketo munchkin js files into the page header if they're not already loaded there
   * Once loaded, a page visit will be logged to marketo
   */
  const loadMarketoMunchkin = () => {
    if (!window.enableMarketoMunchkinLogging)
      return;

    if (checkMarketoMunchkinReady())
      return;

    const script = document.createElement('script');
    script.id = marketoMunchkinScriptId
    script.type = 'text/javascript';
    script.async = true;
    script.src = '//munchkin.marketo.net/munchkin.js';
    script.onload = () => {
      //Setting apiOnly to true will ensure that page visits won't be automatically logged on init
      //Instead, logs will be controlled by logMarketoMunchkinPageVisit
      const config = { apiOnly: true };
      if (typeof (Munchkin) !== "undefined" && Munchkin !== null) {
        Munchkin.init(window.marketoMunchkinId, config);
        marketoMunchkinReadyRef.current = true;
        logMarketoMunchkinPageVisit(window.location.pathname);
      }
    }
    document.getElementsByTagName('head')[0].appendChild(script);
  };

  const triggerFirstPendoSnippet = () => {
    if (window.environment !== DeploymentEnvironmentConstants.Local) {
      const snippet = document.createElement("script");
      snippet.innerHTML = `
        (function(apiKey){
              (function (p, e, n, d, o) {
                var v, w, x, y, z; o = p[d] = p[d] || {}; o._q = o._q || [];
                v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track']; for (w = 0, x = v.length; w < x; ++w)(function (m) {
                  o[m] = o[m] || function () { o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0))); };
                })(v[w]);
                y = e.createElement(n); y.async = !0; y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
                z = e.getElementsByTagName(n)[0]; z.parentNode.insertBefore(y, z);
              })(window, document, 'script', 'pendo');
        })('${window.pendoApiKey}');`;

      document.body.appendChild(snippet);
    }
  };

  const triggerSecondPendoSnippet = (pendoUser: UserInfo, pendoClient: Client) => {
    if (window.environment === DeploymentEnvironmentConstants.Local || !pendoUser)
      return;

    const snippet = document.createElement("script");
    if (!!pendoUser.email && !pendoUser.email.endsWith("@questanalytics.com")) {
      snippet.innerHTML = `
        pendo.initialize({
          visitor: {
            id: '${pendoUser.userId}',
            email: '${JSON.stringify(pendoUser.email)}',
            full_name: '${JSON.stringify(pendoUser.displayName)}',
            environment: '${JSON.stringify(window.environment)}',
            roles: '${JSON.stringify(pendoUser.roles.sort().join(", "))}',
            viewport_height: '${window.innerHeight}',
            viewport_width: '${window.innerWidth}',
          },
          account: {
            id: '${pendoUser.clientId}',
            name: '${JSON.stringify(pendoUser.clientName)}',
            sf_account_id: '${pendoClient.sfAccountId}',
            isActive: '${pendoClient.isActive}',
            deactivated_date: '${!pendoClient.dateDeactivated ? "" : pendoClient.dateDeactivated}',
            roles: '${JSON.stringify(pendoClient.roles.map(r => r.name).sort().join(", "))}'
          }
        });`;
    } else {
      snippet.innerHTML = `
        pendo.initialize({
          visitor: {
            id: ''
          }
        });`;
    }
    document.body.appendChild(snippet);
  };

  const handleUserSet = (loadedUser: UserInfo, loadedClient: Client) => {
    if (!pendoHasInitialized.current) {
      triggerSecondPendoSnippet(loadedUser, loadedClient);
    }
    datadogRum.setUser({
      clientId: loadedUser.clientId,
      email: loadedUser.name,
      id: loadedUser.userId.toString(),
      name: loadedUser.clientName,
    });
  };

  const getDefaultDashboard = (dashboardUser: UserInfo) => {
    let defaultDashboard = DashboardType.Unknown;

    if (dashboardUser.authorizedDashboardTypes.length == 0)
      return defaultDashboard;

    const projDash = dashboardUser.authorizedDashboardTypes.find(x => x === DashboardType.Project);
    const netAssessDash = dashboardUser.authorizedDashboardTypes.find(x => x === DashboardType.NetworkAssessment);

    if (!!netAssessDash)
      defaultDashboard = netAssessDash;
    else if (!!projDash)
      defaultDashboard = projDash;
    else
      defaultDashboard = dashboardUser.authorizedDashboardTypes[0]

    return defaultDashboard
  };

  /**
   * Uses a combination of browser cookies and UserClaim to determine which dashboard to load for the user
   * After loading a dashboard, the user's selection is stored in cookies and in UserClaim
   * @param dashboardUser
   * @return true/false to indicate if we're redirecting to a new page to get to the specified dashboard
   */
  const loadDashboard = async (dashboardUser: UserInfo) => {
    let currentDashboard: DashboardType = DashboardType.Unknown;
    const dashboardCookie = await cookieStore.get("CurrentDashboard");
    if (dashboardCookie?.value in DashboardType) {
      currentDashboard = dashboardCookie.value as DashboardType;
    }

    let dashClaim: UserClaim = null;
    if (currentDashboard === DashboardType.Unknown) {
      let userClaims = await userService.getUserClaims();

      if (userClaims.length > 0)
        userClaims = userClaims.filter(x => x.type === dashboardDefaultClaimType);

      if (userClaims.length > 0)
        dashClaim = userClaims.find(x => x.value in DashboardType);

      if (!!dashClaim) {
        currentDashboard = dashClaim.value as DashboardType;
      } else {
        currentDashboard = getDefaultDashboard(dashboardUser);
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    let requestedDashboard: DashboardType = DashboardType.Unknown;
    if (urlParams.get("Filter") in DashboardType) {
      requestedDashboard = urlParams.get("Filter") as DashboardType
    }

    if (requestedDashboard !== DashboardType.Unknown) {
      currentDashboard = requestedDashboard;
    }

    let path = "";
    let reroute = false;
    switch (currentDashboard) {
      case DashboardType.NetworkAssessment:
        path = "/";
        break;
      case DashboardType.Operations:
        path = "/operations/dashboard";
        break;
      case DashboardType.AccessReports:
        path = "/access-reports";
        break;
      case DashboardType.NetworkOutreach:
        path = "/network-outreach";
        break;
      case DashboardType.ComplianceConnect:
        path = "/compliance-connect";
        break;
      case DashboardType.Unknown:
        path = window.location.pathname;
        break;
      case DashboardType.CACDI:
      case DashboardType.Directory:
      case DashboardType.NYSDOHAdHocProject:
      case DashboardType.Project:
        path = `${window.uiEndpoint}/Home?Filter=${currentDashboard}`;
        reroute = true;
        break;
      default:
        path = `${window.customUIBaseUrl}?Filter=${currentDashboard}`;
        reroute = true;
        break;
    }

    if (currentDashboard !== DashboardType.Unknown) {
      await userService.removeUserClaim({ type: dashboardDefaultClaimType, value: currentDashboard });
      await userService.addUserClaim({ type: dashboardDefaultClaimType, value: currentDashboard });
      await cookieStore.set({
        name: "CurrentDashboard",
        path: "/",
        value: currentDashboard,
      });
    }

    if (reroute) {
      window.location.href = path;
      //Marketo munchkin logging handled in 1.0
    } else if (!!path) {
      browserHistory.replace(path, { isActive: true });
      logMarketoMunchkinPageVisit(path);
    }

    return reroute;
  };

  const validateSession = async () => {
    let validSession = await userService.validateAndUpdateSession();

    if (!validSession) {
      try {
        await AuthenticationService.getInstance().renewToken();
        validSession = await userService.validateAndUpdateSession();

        if (!validSession) {
          AuthenticationService.getInstance().logout("InvalidSession");
          return;
        }
      } catch {
        AuthenticationService.getInstance().logout("InvalidSession");
        return;
      }
    }

    setTimeout(validateSession, sessionValidationIntervalSeconds * 1000);
  };

  const handleSessionEnded = (event: authentication.SessionEvent) => {
    const sessionUser = AuthenticationService.getInstance().getUser();
    if (sessionUser && event.userId.toString() === sessionUser.profile.sub && event.sessionId === sessionUser.profile.sid) {
      AuthenticationService.getInstance().logout("InvalidSession");
    }
  };

  useEffect(() => {
    let unlisten: UnregisterCallback;
    let signalrConnection: authentication.HubConnection;

    (async () => {
      if (!pendoHasInitialized.current) {
        triggerFirstPendoSnippet();
      }
      loadMarketoMunchkin();
      let signedIn = false;
      try {
        await AuthenticationService.getInstance().loadUser();
        // see if user is signed in. Prompts the user to sign in if not already
        // probably returns false if the user failed to sign in. Maybe.
        signedIn = AuthenticationService.getInstance().isUserSignedIn();

        if (!signedIn) { // not signed in
          // goofy special case - if the /callback or /silent-refresh-callback endpoint is being invoked, let it pass through without an authenticated user
          // otherwise, login. There's probably a better way to do this...
          if (window.location.pathname.indexOf("/callback") === -1 && window.location.pathname.indexOf("/silent-refresh-callback") === -1) {
            await AuthenticationService.getInstance().login();
          }

          setReady(true);
          setUser(undefined);
          return;
        } else { // user is already logged in

          // not sure how else to accomplish this, but this gives the identity server time to sync up
          // with any changes made while the user was in 1.0 (e.g. switched to a guest user) before
          // validating the session
          validationTimeout.current = window.setTimeout(async () => {
            await validateSession(); // will logout and redirect if session is not valid
          }, 2000);

          const loadedUser = await userService.get();
          const loadedClient = await clientService.getClient(loadedUser.clientId);
          loadedUser.clientType = loadedClient.clientType;
          loadedUser.regulatorClientIds = loadedClient.regulatorClientIds ?? [];
          if (loadedClient.clientType === ClientType.TwoSidedRegulator && !loadedUser.authorizedDashboardTypes.includes(DashboardType.ComplianceConnect)) {
            loadedUser.authorizedDashboardTypes.push(DashboardType.ComplianceConnect);
          }

          signalrConnection = new authentication.HubConnectionBuilder().getConnection();
          signalrConnection.subscribe("sessionEnded", handleSessionEnded);

          //If loadDashboard is redirecting us to a new page, then there's no need to ready this page
          //Only load dashboard when navigating to the home page
          const isReady = window.location.pathname === "/app/v2.0" ? !(await loadDashboard(loadedUser)) : true;

          setReady(isReady);
          setUser(loadedUser);
          handleUserSet(loadedUser, loadedClient);
        }
      } catch (e) {
        setReady(true);
        setUser(undefined);
      } finally {
        // moving this out of the try so if this rejects it doesn't kick the user
        if (signedIn) {
          try {
            await cookieStore.set("LastLoggedInDate", (await userService.getLastLoginDate()).toString());
          } catch { }
        }
      }

      // handles the initially loaded view
      datadogRum.startView(datadogViewNames(window.location.pathname));
      // and this will handle every view change
      unlisten = browserHistory.listen(({ pathname }) => {
        datadogRum.startView(datadogViewNames(pathname));
        logMarketoMunchkinPageVisit(pathname);
      });
    })();

    return () => {
      window.clearTimeout(validationTimeout.current);
      unlisten?.();
      signalrConnection?.unsubscribe("sessionEnded", handleSessionEnded);
    };
  }, []);

  const renderAppBody = () => {
    if (user && user.showMaintenancePage && !user.roles.includes(Roles.MAINTENANCE_ADMIN)) {
      return (
        <MaintenancePage />
      );
    } else {
      return (
        <>
          <Router history={browserHistory}>
            <Layout>
              <ErrorBoundary user={user}>
                {ready && !!user ?
                  (
                    <FeatureFlagContext.Consumer>
                      {({ enabledFeatureFlags }) => (
                        <Switch>
                          <DashboardRestrictedRoute allowedDashboards={[DashboardType.NetworkAssessment]} exact={true} path="/" component={Home} />
                          <Route exact={true} path="/" component={Home} />
                          <Route exact={true} path="/callback" component={AuthenticationCallback} />
                          <Route exact={true} path="/silent-refresh-callback" component={SilentRefreshCallback} />
                          <Route path="/access-reports" component={AccessReportsRouter} />
                          <Route path="/account" component={AccountRouter} />
                          <Route path="/projects" component={ProjectsRouter} />
                          <Route path="/maintenance" component={MaintenanceRouter} />
                          <Route path="/member-populations" component={MemberSetsRouter} />
                          <Route path="/tutorials" component={TutorialsRouter} />
                          <Route path="/templates" component={TemplatesRouter} />
                          <Route path="/tools" component={ToolsRouter} />
                          <Route path="/targeting" component={TargetingRouter} />
                          <Route path="/tags" component={TagsRouter} />
                          <Route path="/terminations" component={TerminationsRouter} />
                          <Route path="/provider-network-import" component={ProviderNetworkImportRouter} />
                          <Route path="/reports" component={ReportsRouter} />
                          <Route path="/admin" component={AdminRouter} />
                          <Route path="/performance" component={PerformanceRouter} />
                          <Route path="/provider-sets" component={ProviderSetsRouter} />
                          <Route path="/overviews" component={OverviewsRouter} />
                          <DashboardRestrictedRoute allowedDashboards={[DashboardType.Operations]} path="/operations" component={OperationsRouter} />
                          <DashboardRestrictedRoute allowedDashboards={[DashboardType.NetworkOutreach]} path="/network-outreach" component={NetworkOutreachRouter} />
                          <DashboardRestrictedRoute allowedDashboards={[DashboardType.ComplianceConnect]} path="/compliance-connect" component={ComplianceConnectRegulatorRouter} />
                          <Route path="/resources" component={ResourcesRouter} />
                          <Route path="/exceptions" component={ExceptionsRouter} />
                          {enabledFeatureFlags.has(Features.TWOSIDED_ISSUER_WORKFLOW) &&
                            <Route path="/network-submission" component={NetworkSubmissionRouter} />
                          }
                          {enabledFeatureFlags.has(Features.ADEQUACY_REPORT_CUSTOMIZATION) &&
                            <Route path="/report-presets" component={ManageReportPresets} />
                          }
                          <Route component={NotFound} />
                        </Switch>
                      )}
                    </FeatureFlagContext.Consumer>
                  )
                  : (
                    <div className="pt-3 ps-3">
                      <Switch>
                        <Route exact={true} path="/callback" component={AuthenticationCallback} />
                        <Route exact={true} path="/silent-refresh-callback" component={SilentRefreshCallback} />
                        <Route path="/" component={LoadingSpinner} />
                      </Switch>
                    </div>
                  )
                }
              </ErrorBoundary>
            </Layout>
          </Router>

          <ToastContainer
            containerId="z-index-9999"
            closeOnClick={false}
            newestOnTop={true}
            pauseOnFocusLoss={false}
            position="top-right"
          />
          <ToastContainer
            containerId="z-index-1030"
            style={{ zIndex: 1030 }}
          />
        </>
      );
    }
  };

  return user ?
    (
      <QueryClientProvider client={queryClient}>
        <UserContext.Provider value={{ ...user, updateClientType }}>
          <NotificationHandlerWrapper>
            <FeatureFlagContextProvider>
              <MappingContextProvider>
                {renderAppBody()}
              </MappingContextProvider>
            </FeatureFlagContextProvider>
            <InactivityMonitor />
          </NotificationHandlerWrapper>
        </UserContext.Provider>
      </QueryClientProvider>
    )
    :
    (
      <UserContext.Provider value={user}>
        {renderAppBody()}
      </UserContext.Provider>
    );
}

export default provideNotificationContext(App);
